import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PortalLayout } from '../components/PortalLayout';

interface QuizQuestion {
  id: string;
  level: string;
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

interface QuizConfig {
  passing_score: number;
  questions_per_attempt: number;
  min_seconds_per_question: number;
  total_pool: number;
  questions: QuizQuestion[];
}

interface Module {
  id: string;
  slug: string;
  title: string;
  category: string;
  duration_minutes: number | null;
  content_url: string | null;
  quiz_questions: QuizConfig | null;
  pass_threshold_pct: number;
}

interface Completion {
  id: string;
  module_id: string;
  quiz_score_pct: number | null;
  passed: boolean | null;
  attempts: number;
}

interface QuizResult {
  question_id: string;
  correct: boolean;
  correct_index: number;
  explanation: string;
}

interface QuizResponse {
  score_pct: number;
  passed: boolean;
  correct_count: number;
  total: number;
  passing_score: number;
  results: QuizResult[];
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  product: { bg: 'rgba(59, 130, 246, 0.15)', text: '#60a5fa' },
  sales: { bg: 'rgba(16, 185, 129, 0.15)', text: '#34d399' },
  technical: { bg: 'rgba(168, 85, 247, 0.15)', text: '#c084fc' },
  compliance: { bg: 'rgba(245, 158, 11, 0.15)', text: '#fbbf24' },
};

function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function TrainingModule() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [mod, setMod] = useState<Module | null>(null);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const [loading, setLoading] = useState(true);

  const [quizMode, setQuizMode] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Map<string, number>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizResponse | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/partner/training', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const courses = data.courses as Array<{
          modules: Array<{ module_id: string; module: Module | null }>;
        }>;
        const completions = (data.completions ?? []) as Completion[];

        let foundModule: Module | null = null;
        for (const course of courses) {
          for (const cm of course.modules) {
            if (cm.module && cm.module.slug === slug) {
              foundModule = cm.module;
              break;
            }
          }
          if (foundModule) break;
        }

        if (foundModule) {
          setMod(foundModule);
          const comp = completions.find(c => c.module_id === foundModule!.id);
          if (comp) setCompletion(comp);
        }
      } catch {
        // fail silently
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  const startQuiz = useCallback(() => {
    if (!mod?.quiz_questions) return;
    const quiz = mod.quiz_questions;
    const pool = shuffleArray(quiz.questions);
    const selected = pool.slice(0, quiz.questions_per_attempt);
    setQuizQuestions(selected);
    setCurrentQ(0);
    setAnswers(new Map());
    setQuizResult(null);
    setQuizMode(true);
  }, [mod]);

  const selectAnswer = useCallback((questionId: string, index: number) => {
    setAnswers(prev => {
      const next = new Map(prev);
      next.set(questionId, index);
      return next;
    });
  }, []);

  const submitQuiz = useCallback(async () => {
    if (!mod) return;
    setSubmitting(true);
    try {
      const answerPayload = quizQuestions.map(q => ({
        question_id: q.id,
        selected_index: answers.get(q.id) ?? -1,
      })).filter(a => a.selected_index >= 0);

      const res = await fetch('/api/partner/training/quiz', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          module_id: mod.id,
          answers: answerPayload,
        }),
      });

      if (res.ok) {
        const result = (await res.json()) as QuizResponse;
        setQuizResult(result);
        if (result.passed && completion) {
          setCompletion({ ...completion, passed: true, quiz_score_pct: result.score_pct, attempts: completion.attempts + 1 });
        } else if (result.passed) {
          setCompletion({ id: '', module_id: mod.id, passed: true, quiz_score_pct: result.score_pct, attempts: 1 });
        }
      }
    } catch {
      // fail silently
    }
    setSubmitting(false);
  }, [mod, quizQuestions, answers, completion]);

  const allAnswered = useMemo(() => {
    return quizQuestions.every(q => answers.has(q.id));
  }, [quizQuestions, answers]);

  if (loading) {
    return (
      <PortalLayout>
        <div style={{ padding: '2rem', color: '#94a3b8' }}>Loading module...</div>
      </PortalLayout>
    );
  }

  if (!mod) {
    return (
      <PortalLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>Module not found.</p>
          <button
            onClick={() => navigate('/training')}
            style={{ color: '#67e8f9', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Back to Training
          </button>
        </div>
      </PortalLayout>
    );
  }

  const catColor = CATEGORY_COLORS[mod.category] || { bg: 'rgba(255,255,255,0.08)', text: '#94a3b8' };
  const isPassed = completion?.passed === true;

  return (
    <PortalLayout>
      <div style={{ maxWidth: 800, padding: '1rem', margin: '0 auto' }}>
        {/* Back link */}
        <button
          onClick={() => navigate('/training')}
          style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem', padding: 0 }}
        >
          &larr; Back to Training
        </button>

        {/* Module header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: 8 }}>
            <span style={{
              fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4,
              background: catColor.bg, color: catColor.text, textTransform: 'uppercase', fontWeight: 600,
            }}>
              {mod.category}
            </span>
            {mod.duration_minutes && (
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>~{mod.duration_minutes} min</span>
            )}
            {isPassed && (
              <span style={{
                fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4,
                background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontWeight: 600,
              }}>
                PASSED {completion?.quiz_score_pct}%
              </span>
            )}
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{mod.title}</h1>
        </div>

        {/* Video player */}
        {mod.content_url && !quizMode && (
          <div style={{
            marginBottom: '1.5rem',
            background: '#000',
            borderRadius: 8,
            overflow: 'hidden',
            aspectRatio: '16/9',
          }}>
            <video
              src={mod.content_url}
              controls
              preload="metadata"
              style={{ width: '100%', height: '100%', display: 'block' }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* Quiz section */}
        {!quizMode && mod.quiz_questions && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            padding: '1.5rem',
            textAlign: 'center',
          }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Module Quiz</h2>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 1rem' }}>
              {mod.quiz_questions.questions_per_attempt} questions, {mod.quiz_questions.passing_score}% to pass.
              {completion && ` ${completion.attempts} attempt${completion.attempts > 1 ? 's' : ''} so far.`}
            </p>
            <button
              onClick={startQuiz}
              style={{
                padding: '0.6rem 1.5rem',
                background: isPassed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(103, 232, 249, 0.15)',
                color: isPassed ? '#34d399' : '#67e8f9',
                border: `1px solid ${isPassed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(103, 232, 249, 0.3)'}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {isPassed ? 'Retake Quiz' : completion ? 'Retry Quiz' : 'Start Quiz'}
            </button>
          </div>
        )}

        {/* Quiz in progress */}
        {quizMode && !quizResult && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            padding: '1.5rem',
          }}>
            {/* Progress */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                Question {currentQ + 1} of {quizQuestions.length}
              </span>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                {answers.size}/{quizQuestions.length} answered
              </span>
            </div>

            {/* Progress bar */}
            <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginBottom: '1.5rem', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${((currentQ + 1) / quizQuestions.length) * 100}%`,
                background: '#67e8f9',
                borderRadius: 2,
                transition: 'width 0.3s',
              }} />
            </div>

            {/* Current question */}
            {quizQuestions[currentQ] && (() => {
              const q = quizQuestions[currentQ];
              const selected = answers.get(q.id);

              return (
                <div>
                  <p style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '1rem', lineHeight: 1.5 }}>
                    {q.question}
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {q.options.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectAnswer(q.id, idx)}
                        style={{
                          padding: '0.75rem 1rem',
                          background: selected === idx ? 'rgba(103, 232, 249, 0.12)' : 'rgba(255,255,255,0.03)',
                          border: selected === idx ? '1px solid rgba(103, 232, 249, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 6,
                          color: selected === idx ? '#67e8f9' : '#e2e8f0',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '0.85rem',
                          lineHeight: 1.4,
                          transition: 'all 0.15s',
                        }}
                      >
                        <span style={{ fontWeight: 600, marginRight: 8, opacity: 0.5 }}>
                          {String.fromCharCode(65 + idx)}.
                        </span>
                        {opt}
                      </button>
                    ))}
                  </div>

                  {/* Navigation */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <button
                      onClick={() => setCurrentQ(p => Math.max(0, p - 1))}
                      disabled={currentQ === 0}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        color: currentQ === 0 ? '#475569' : '#94a3b8',
                        cursor: currentQ === 0 ? 'default' : 'pointer',
                        fontSize: '0.85rem',
                      }}
                    >
                      Previous
                    </button>

                    {currentQ < quizQuestions.length - 1 ? (
                      <button
                        onClick={() => setCurrentQ(p => Math.min(quizQuestions.length - 1, p + 1))}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(103, 232, 249, 0.1)',
                          border: '1px solid rgba(103, 232, 249, 0.3)',
                          borderRadius: 6,
                          color: '#67e8f9',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={submitQuiz}
                        disabled={!allAnswered || submitting}
                        style={{
                          padding: '0.5rem 1.5rem',
                          background: allAnswered ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${allAnswered ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: 6,
                          color: allAnswered ? '#34d399' : '#475569',
                          cursor: allAnswered && !submitting ? 'pointer' : 'default',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        {submitting ? 'Submitting...' : 'Submit Quiz'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Quiz results */}
        {quizResult && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            padding: '1.5rem',
          }}>
            {/* Score header */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80, height: 80,
                borderRadius: '50%',
                border: `3px solid ${quizResult.passed ? '#34d399' : '#f87171'}`,
                marginBottom: '0.75rem',
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: quizResult.passed ? '#34d399' : '#f87171' }}>
                  {quizResult.score_pct}%
                </span>
              </div>
              <h2 style={{
                fontSize: '1.2rem', fontWeight: 600, margin: '0 0 0.25rem',
                color: quizResult.passed ? '#34d399' : '#f87171',
              }}>
                {quizResult.passed ? 'Passed!' : 'Not Passed'}
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                {quizResult.correct_count}/{quizResult.total} correct (need {quizResult.passing_score}%)
              </p>
            </div>

            {/* Question-by-question results */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {quizQuestions.map((q, idx) => {
                const result = quizResult.results.find(r => r.question_id === q.id);
                const userAnswer = answers.get(q.id);
                if (!result) return null;

                return (
                  <div key={q.id} style={{
                    padding: '1rem',
                    background: result.correct ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                    border: `1px solid ${result.correct ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`,
                    borderRadius: 6,
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <span style={{
                        flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 700,
                        background: result.correct ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: result.correct ? '#34d399' : '#f87171',
                      }}>
                        {result.correct ? '✓' : '✗'}
                      </span>
                      <p style={{ fontSize: '0.85rem', margin: 0, lineHeight: 1.4 }}>
                        <strong>Q{idx + 1}.</strong> {q.question}
                      </p>
                    </div>

                    {!result.correct && (
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginLeft: 30 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: '#f87171' }}>Your answer:</span> {q.options[userAnswer ?? 0]}
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: '#34d399' }}>Correct:</span> {q.options[result.correct_index]}
                        </div>
                      </div>
                    )}

                    <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '8px 0 0 30px', fontStyle: 'italic' }}>
                      {result.explanation}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
              {!quizResult.passed && (
                <button
                  onClick={startQuiz}
                  style={{
                    padding: '0.6rem 1.5rem',
                    background: 'rgba(103, 232, 249, 0.15)',
                    color: '#67e8f9',
                    border: '1px solid rgba(103, 232, 249, 0.3)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  Retry Quiz
                </button>
              )}
              <button
                onClick={() => navigate('/training')}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Back to Training
              </button>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
